import React from "react";

/**
 * 답변 표의 가로 스크롤 박스. 더 볼 내용이 있는 쪽 끝을 흐리게 해서 알리고,
 * 스크롤하면 표가 본문 왼쪽 밖(채팅 화면 왼쪽 여백)까지 넘어간다. 폭과 여백은 index.css 가 정한다.
 */
type Edge = "none" | "start" | "end" | "both";

/* 넓은 표가 스크롤로 넘어갈 수 있는 왼쪽 끝. 채팅 화면(data-table-pane) 왼쪽에서 이만큼 안쪽이다. */
const PANE_GUTTER = 16;

/**
 * 표 영역이 본문 왼쪽 밖으로 넓힐 거리(--bleed). 채팅 화면 왼쪽 여백까지이고 실제 위치로 잰다.
 * 목록 안에 들여쓴 표도 화면 밖으로 넘치지 않는다. 기준 영역이 없으면(스토리북 등) 넓히지 않는다.
 */
export const measureBleed = (frame: HTMLElement): number | undefined => {
  const pane = frame.closest("[data-table-pane]");
  if (pane === null) return undefined;
  /* 지금 걸린 음수 여백만큼 이미 왼쪽으로 와 있어서 되돌려 원래 자리를 구한다. */
  const current = Number.parseFloat(frame.style.getPropertyValue("--bleed"));
  const left =
    frame.getBoundingClientRect().left + (Number.isNaN(current) ? 0 : current);
  return Math.max(
    0,
    Math.floor(left - pane.getBoundingClientRect().left - PANE_GUTTER),
  );
};

const TableScrollBox = ({ children }: { children?: React.ReactNode }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [edge, setEdge] = React.useState<Edge>("none");

  /* 더 볼 내용이 있는 쪽 끝. 스크롤할 때마다 다시 본다. */
  const updateEdge = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    /* 1px 은 소수점 폭 때문에 생기는 오차 여유다. */
    if (max <= 1) {
      setEdge("none");
      return;
    }
    if (el.scrollLeft <= 1) setEdge("end");
    else if (el.scrollLeft >= max - 1) setEdge("start");
    else setEdge("both");
  }, []);

  /* 왼쪽 여백은 채팅 화면 폭이 바뀔 때만 다시 잰다. 스크롤로는 바뀌지 않는다. */
  const updateBleed = React.useCallback(() => {
    const frame = ref.current?.parentElement;
    const bleed = frame ? measureBleed(frame) : undefined;
    if (frame && bleed !== undefined)
      frame.style.setProperty("--bleed", `${bleed}px`);
  }, []);

  /* 첫 그림 전에 여백을 넣어 긴 글 칸이 한 번 접혔다가 다시 펴지는 튐을 막는다. */
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    updateBleed();
    updateEdge();
    /* 창 크기와 표 크기(View more 로 행이 늘어나는 경우) 둘 다 본다. */
    const observer = new ResizeObserver(() => {
      updateBleed();
      updateEdge();
    });
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    /* 사이드바를 접거나 근거 패널을 열면 채팅 화면 폭이 바뀐다. 높이만 바뀔 때(스트리밍)는 건너뛴다. */
    const pane = el.closest("[data-table-pane]");
    let paneWidth = -1;
    const paneObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? -1;
      if (width === paneWidth) return;
      paneWidth = width;
      updateBleed();
      updateEdge();
    });
    if (pane) paneObserver.observe(pane);
    return () => {
      observer.disconnect();
      paneObserver.disconnect();
    };
  }, [updateBleed, updateEdge]);

  return (
    <div className="answer-table-frame">
      <div
        ref={ref}
        className="answer-table-box"
        data-scroll-hint={edge}
        onScroll={updateEdge}
      >
        {children}
      </div>
    </div>
  );
};

export default TableScrollBox;
